/**
 * Per-position rule lines for the position chart (gold v2 phase 3b).
 *
 * The Discipline engine computes every rule's line on the server (glossary
 * rule_line: one Black-Scholes in backend/app/metrics/definitions.py, the
 * contract's IV, the capped time stop) and serves it on each rule of
 * GET /positions/{id}/discipline (line_price, trigger_date, line_curve). This
 * module only turns those fields into chart primitives — it prices nothing,
 * copies no engine constant and never re-derives a date.
 *
 *   stockStopPrice:       horizontal line (rule stock_stop)
 *   strikeProximityPrice: horizontal line (rule strike_proximity)
 *   timeStopTimestamp:    vertical line at the capped time-stop date (rule time_stop)
 *   profitTargetCurve:    the profit-target line over time (rule profit_target)
 *   deltaBreachCurve:     the defensive-delta line over time (rule defensive_delta)
 */
import { DisciplineState } from '../types/discipline';

/** 4:00 pm New York (the options close) on an ISO date, as UNIX millis.
 *  One clock for every date on the chart: the rule lines, the expiry line and
 *  marker. (new Date('YYYY-MM-DD') is midnight UTC = 8 pm ET the day BEFORE.) */
export function expiryCloseMs(isoDate: string): number {
  const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
  // 16:00 ET = 20:00 UTC in daylight time, 21:00 UTC in standard time.
  const edt = Date.UTC(y, m - 1, d, 20);
  const nyHour = Number(new Date(edt).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }));
  return nyHour === 16 ? edt : Date.UTC(y, m - 1, d, 21);
}

export interface BoundaryCurvePoint {
  /** UNIX millis — matches the chart's `timestamp` axis. */
  timestamp: number;
  /** Stock price at which the rule fires on that date. */
  stockPrice: number;
}

export interface PositionBoundaries {
  stockStopPrice: number | null;
  timeStopTimestamp: number | null;
  profitTargetCurve: BoundaryCurvePoint[] | null;
  deltaBreachCurve: BoundaryCurvePoint[] | null;
  strikeProximityPrice?: number | null;
  /** True when the engine evaluates an option-priced rule but could not draw
   *  its line (no implied volatility on the contract) — the chart says so. */
  ivUnavailable: boolean;
}

const curve = (pts: Array<{ date: string; price: number }> | null | undefined): BoundaryCurvePoint[] | null =>
  pts && pts.length >= 2 ? pts.map((p) => ({ timestamp: expiryCloseMs(p.date), stockPrice: p.price })) : null;

/** The engine's rule lines as chart primitives; null when no rule state yet. */
export function boundariesFromRules(d: DisciplineState | null | undefined): PositionBoundaries | null {
  if (!d?.rules) return null;
  const rule = (key: string) => d.rules!.find((r) => r.key === key);
  const pt = rule('profit_target');
  const dd = rule('defensive_delta');
  const ts = rule('time_stop');
  return {
    stockStopPrice: rule('stock_stop')?.line_price ?? null,
    strikeProximityPrice: rule('strike_proximity')?.line_price ?? null,
    timeStopTimestamp: ts?.trigger_date ? expiryCloseMs(ts.trigger_date) : null,
    profitTargetCurve: curve(pt?.line_curve),
    deltaBreachCurve: curve(dd?.line_curve),
    ivUnavailable: (!!pt && !pt.line_curve) || (!!dd && !dd.line_curve),
  };
}

export interface ChartPoint { t: number; v: number }

/**
 * Where the profit-target and defensive-delta lines meet (drawing only).
 * Before that point there is a "hold" band between them (call: profit line
 * below the delta line; put: above it); from that point on, every price fires
 * one of the two rules, so the chart stops both lines there. Linear
 * interpolation between the shared samples. Returns null when they never meet
 * before the last shared sample; a crossing at the first sample means there is
 * no hold band.
 */
export function ruleLinesMeet(pt: ChartPoint[], db: ChartPoint[], isPut: boolean): ChartPoint | null {
  const dbAt = new Map(db.map((p) => [p.t, p.v]));
  const shared = pt.filter((p) => dbAt.has(p.t)).sort((a, b) => a.t - b.t);
  // gap > 0 while the hold band is open
  const gap = (p: ChartPoint) => (isPut ? p.v - (dbAt.get(p.t) as number) : (dbAt.get(p.t) as number) - p.v);
  for (let i = 0; i < shared.length; i++) {
    const g = gap(shared[i]);
    if (g > 0) continue;
    if (i === 0) return { t: shared[0].t, v: shared[0].v };
    const a = shared[i - 1], b = shared[i];
    const ga = gap(a);
    const f = ga / (ga - g);
    return { t: a.t + f * (b.t - a.t), v: a.v + f * (b.v - a.v) };
  }
  return null;
}

/** Points of a curve up to (and ending exactly at) time t. */
export function curveUntil(c: ChartPoint[], end: ChartPoint): ChartPoint[] {
  return [...c.filter((p) => p.t < end.t), end];
}

/** A curve's own (linearly interpolated) value at time t; null outside it. */
export function curveValueAt(c: ChartPoint[], t: number): number | null {
  if (c.length === 0 || t < c[0].t || t > c[c.length - 1].t) return null;
  for (let i = 1; i < c.length; i++) {
    const a = c[i - 1], b = c[i];
    if (t <= b.t) return b.t === a.t ? b.v : a.v + ((t - a.t) / (b.t - a.t)) * (b.v - a.v);
  }
  return c[c.length - 1].v;
}
